import { UpgradeDefinition, UpgradeId, Decimal } from '../types'

export const upgrades: ReadonlyMap<UpgradeId, UpgradeDefinition> = new Map([
  // Click upgrades (doublers based on total clicks)
  [
    'mechanicalKeyboard',
    {
      id: 'mechanicalKeyboard',
      name: 'Mechanical Keyboard',
      cost: new Decimal(200),
      requirement: { type: 'clickCount', count: 100 },
      effect: { type: 'doubler', targetBuilding: 'click' },
      description: 'Satisfying clacks. 2x click power.',
    },
  ],
  [
    'dualMonitors',
    {
      id: 'dualMonitors',
      name: 'Dual Monitors',
      cost: new Decimal(5000),
      requirement: { type: 'clickCount', count: 500 },
      effect: { type: 'doubler', targetBuilding: 'click' },
      description: 'More screen, more productivity. 2x click power.',
    },
  ],
  [
    'ultrawideUpgrade',
    {
      id: 'ultrawideUpgrade',
      name: 'Ultrawide Upgrade',
      cost: new Decimal(50000),
      requirement: { type: 'clickCount', count: 1000 },
      effect: { type: 'doubler', targetBuilding: 'click' },
      description: 'One big screen to rule them all. 2x click power.',
    },
  ],
  [
    'standingDesk',
    {
      id: 'standingDesk',
      name: 'Standing Desk',
      cost: new Decimal(500000),
      requirement: { type: 'clickCount', count: 5000 },
      effect: { type: 'doubler', targetBuilding: 'click' },
      description: 'Improved blood flow to brain. 2x click power.',
    },
  ],
  [
    'rgbEverything',
    {
      id: 'rgbEverything',
      name: 'RGB Everything',
      cost: new Decimal(50000000),
      requirement: { type: 'clickCount', count: 50000 },
      effect: { type: 'doubler', targetBuilding: 'click' },
      description: 'Faster photons = faster code. 2x click power.',
    },
  ],
  [
    'carpalTunnelBrace',
    {
      id: 'carpalTunnelBrace',
      name: 'Carpal Tunnel Brace',
      cost: new Decimal(500000),
      requirement: { type: 'buildingCount', buildingId: 'codingAssistant', count: 40 },
      effect: { type: 'synergy', targetBuilding: 'click', sourceBuilding: 'codingAssistant', bonusPercentage: 1 },
      description: 'They type so you don\'t have to. +1% click per Coding Assistant.',
    },
  ],

  // Coding Assistant upgrades
  [
    'betterPrompts',
    {
      id: 'betterPrompts',
      name: 'Better Prompts',
      cost: new Decimal(1000),
      requirement: { type: 'buildingCount', buildingId: 'codingAssistant', count: 5 },
      effect: { type: 'doubler', targetBuilding: 'codingAssistant' },
      description: 'Be concise. Think step by step. 2x production.',
    },
  ],
  [
    'longerContext',
    {
      id: 'longerContext',
      name: 'Longer Context',
      cost: new Decimal(5000),
      requirement: { type: 'buildingCount', buildingId: 'codingAssistant', count: 10 },
      effect: { type: 'doubler', targetBuilding: 'codingAssistant' },
      description: 'Remember the whole codebase. 2x production.',
    },
  ],
  [
    'customInstructions',
    {
      id: 'customInstructions',
      name: 'Custom Instructions',
      cost: new Decimal(50000),
      requirement: { type: 'buildingCount', buildingId: 'codingAssistant', count: 25 },
      effect: { type: 'doubler', targetBuilding: 'codingAssistant' },
      description: 'You are a 10x developer... 2x production.',
    },
  ],
  [
    'multiModelEnsemble',
    {
      id: 'multiModelEnsemble',
      name: 'Multi-Model Ensemble',
      cost: new Decimal(500000),
      requirement: { type: 'buildingCount', buildingId: 'codingAssistant', count: 50 },
      effect: { type: 'doubler', targetBuilding: 'codingAssistant' },
      description: 'Ask three, pick the best. 2x production.',
    },
  ],
  [
    'copilotCartel',
    {
      id: 'copilotCartel',
      name: 'Copilot Cartel',
      cost: new Decimal(50000),
      requirement: { type: 'buildingCount', buildingId: 'codingAgent', count: 40 },
      effect: { type: 'synergy', targetBuilding: 'codingAssistant', sourceBuilding: 'codingAgent', bonusPercentage: 1 },
      description: 'Agents share tips with assistants. +1% per Coding Agent.',
    },
  ],

  // Coding Agent upgrades
  [
    'upgradeToSonnet4',
    {
      id: 'upgradeToSonnet4',
      name: 'Upgrade to Sonnet 4',
      cost: new Decimal(10000),
      requirement: { type: 'buildingCount', buildingId: 'codingAgent', count: 5 },
      effect: { type: 'doubler', targetBuilding: 'codingAgent' },
      description: 'Smarter model. 2x production.',
    },
  ],
  [
    'upgradeToOpus5',
    {
      id: 'upgradeToOpus5',
      name: 'Upgrade to Opus 5',
      cost: new Decimal(50000),
      requirement: { type: 'buildingCount', buildingId: 'codingAgent', count: 10 },
      effect: { type: 'doubler', targetBuilding: 'codingAgent' },
      description: 'Even smarter model. 2x production.',
    },
  ],
  [
    'autonomousLoops',
    {
      id: 'autonomousLoops',
      name: 'Autonomous Loops',
      cost: new Decimal(500000),
      requirement: { type: 'buildingCount', buildingId: 'codingAgent', count: 25 },
      effect: { type: 'doubler', targetBuilding: 'codingAgent' },
      description: 'Agent calls itself. 2x production.',
    },
  ],
  [
    'devContainers',
    {
      id: 'devContainers',
      name: 'Dev Containers',
      cost: new Decimal(5000000),
      requirement: { type: 'buildingCount', buildingId: 'codingAgent', count: 50 },
      effect: { type: 'doubler', targetBuilding: 'codingAgent' },
      description: 'Sandboxed environments. 2x production.',
    },
  ],
  [
    'specDrivenDevelopment',
    {
      id: 'specDrivenDevelopment',
      name: 'Spec-Driven Development',
      cost: new Decimal(50000000),
      requirement: { type: 'buildingCount', buildingId: 'codingAgent', count: 100 },
      effect: { type: 'doubler', targetBuilding: 'codingAgent' },
      description: 'Actually reads the ticket. 2x production.',
    },
  ],
  [
    'localKnowledge',
    {
      id: 'localKnowledge',
      name: 'Local Knowledge',
      cost: new Decimal(500000),
      requirement: { type: 'buildingCount', buildingId: 'laptopFarm', count: 40 },
      effect: { type: 'synergy', targetBuilding: 'codingAgent', sourceBuilding: 'laptopFarm', bonusPercentage: 1 },
      description: 'Learns from your local runs. +1% per Used Laptop.',
    },
  ],

  // Used Laptop upgrades
  [
    'mSeriesChips',
    {
      id: 'mSeriesChips',
      name: 'M-Series Chips',
      cost: new Decimal(100000),
      requirement: { type: 'buildingCount', buildingId: 'laptopFarm', count: 5 },
      effect: { type: 'doubler', targetBuilding: 'laptopFarm' },
      description: 'Apple silicon efficiency. 2x production.',
    },
  ],
  [
    'betterThermals',
    {
      id: 'betterThermals',
      name: 'Better Thermals',
      cost: new Decimal(500000),
      requirement: { type: 'buildingCount', buildingId: 'laptopFarm', count: 10 },
      effect: { type: 'doubler', targetBuilding: 'laptopFarm' },
      description: 'Fans on max. 2x production.',
    },
  ],
  [
    'laptopStacking',
    {
      id: 'laptopStacking',
      name: 'Laptop Stacking',
      cost: new Decimal(5000000),
      requirement: { type: 'buildingCount', buildingId: 'laptopFarm', count: 25 },
      effect: { type: 'doubler', targetBuilding: 'laptopFarm' },
      description: 'Vertical integration. 2x production.',
    },
  ],
  [
    'stolenWifi',
    {
      id: 'stolenWifi',
      name: 'Stolen WiFi',
      cost: new Decimal(50000000),
      requirement: { type: 'buildingCount', buildingId: 'laptopFarm', count: 50 },
      effect: { type: 'doubler', targetBuilding: 'laptopFarm' },
      description: 'Neighbor\'s bandwidth. 2x production.',
    },
  ],
  [
    'distributedTraining',
    {
      id: 'distributedTraining',
      name: 'Distributed Training',
      cost: new Decimal(5000000),
      requirement: { type: 'buildingCount', buildingId: 'gpu', count: 40 },
      effect: { type: 'synergy', targetBuilding: 'laptopFarm', sourceBuilding: 'gpu', bonusPercentage: 1 },
      description: 'Laptops feed GPU training. +1% per GPU.',
    },
  ],

  // GPU upgrades
  [
    'usedRax3090',
    {
      id: 'usedRax3090',
      name: 'Used RAX 3090',
      cost: new Decimal(250000),
      requirement: { type: 'buildingCount', buildingId: 'gpu', count: 5 },
      effect: { type: 'doubler', targetBuilding: 'gpu' },
      description: 'Lightly used for mining. 2x production.',
    },
  ],
  [
    'newRax5090',
    {
      id: 'newRax5090',
      name: 'New RAX 5090',
      cost: new Decimal(2500000),
      requirement: { type: 'buildingCount', buildingId: 'gpu', count: 10 },
      effect: { type: 'doubler', targetBuilding: 'gpu' },
      description: 'Shiny new flagship. 2x production.',
    },
  ],
  [
    'overclock',
    {
      id: 'overclock',
      name: 'Overclock',
      cost: new Decimal(25000000),
      requirement: { type: 'buildingCount', buildingId: 'gpu', count: 25 },
      effect: { type: 'doubler', targetBuilding: 'gpu' },
      description: 'Void that warranty. 2x production.',
    },
  ],
  [
    'waterCooling',
    {
      id: 'waterCooling',
      name: 'Water Cooling',
      cost: new Decimal(250000000),
      requirement: { type: 'buildingCount', buildingId: 'gpu', count: 50 },
      effect: { type: 'doubler', targetBuilding: 'gpu' },
      description: 'Tubes everywhere. 2x production.',
    },
  ],
  [
    'cudaOptimization',
    {
      id: 'cudaOptimization',
      name: 'CUDA Optimization',
      cost: new Decimal(2500000000),
      requirement: { type: 'buildingCount', buildingId: 'gpu', count: 100 },
      effect: { type: 'doubler', targetBuilding: 'gpu' },
      description: 'Hand-tuned kernels. 2x production.',
    },
  ],
  [
    'modelDistillation',
    {
      id: 'modelDistillation',
      name: 'Model Distillation',
      cost: new Decimal(50000000),
      requirement: { type: 'buildingCount', buildingId: 'codingAgent', count: 40 },
      effect: { type: 'synergy', targetBuilding: 'gpu', sourceBuilding: 'codingAgent', bonusPercentage: 1 },
      description: 'Agents fine-tune local models. +1% per Coding Agent.',
    },
  ],
  [
    'miningRigConversion',
    {
      id: 'miningRigConversion',
      name: 'Mining Rig Conversion',
      cost: new Decimal(10000000),
      requirement: { type: 'buildingCount', buildingId: 'laptopFarm', count: 40 },
      effect: { type: 'synergy', targetBuilding: 'gpu', sourceBuilding: 'laptopFarm', bonusPercentage: 1 },
      description: 'Repurposed hardware. +1% per Used Laptop.',
    },
  ],
  [
    'multiGpuRig',
    {
      id: 'multiGpuRig',
      name: 'Multi-GPU Rig',
      cost: new Decimal(5000000),
      requirement: { type: 'buildingCount', buildingId: 'gpu', count: 40 },
      effect: { type: 'synergy', targetBuilding: 'gpu', sourceBuilding: 'gpu', bonusPercentage: 1 },
      description: 'Gang them together. +1% per GPU.',
    },
  ],

  // Cloud Cluster upgrades
  [
    'spotInstances',
    {
      id: 'spotInstances',
      name: 'Spot Instances',
      cost: new Decimal(10000000),
      requirement: { type: 'buildingCount', buildingId: 'cloudCluster', count: 5 },
      effect: { type: 'doubler', targetBuilding: 'cloudCluster' },
      description: 'Cheap but might disappear. 2x production.',
    },
  ],
  [
    'reservedCapacity',
    {
      id: 'reservedCapacity',
      name: 'Reserved Capacity',
      cost: new Decimal(50000000),
      requirement: { type: 'buildingCount', buildingId: 'cloudCluster', count: 10 },
      effect: { type: 'doubler', targetBuilding: 'cloudCluster' },
      description: 'Locked in pricing. 2x production.',
    },
  ],
  [
    'multiRegion',
    {
      id: 'multiRegion',
      name: 'Multi-Region',
      cost: new Decimal(500000000),
      requirement: { type: 'buildingCount', buildingId: 'cloudCluster', count: 25 },
      effect: { type: 'doubler', targetBuilding: 'cloudCluster' },
      description: 'Servers everywhere. 2x production.',
    },
  ],
  [
    'kubernetes',
    {
      id: 'kubernetes',
      name: 'Kubernetes',
      cost: new Decimal(5000000000),
      requirement: { type: 'buildingCount', buildingId: 'cloudCluster', count: 50 },
      effect: { type: 'doubler', targetBuilding: 'cloudCluster' },
      description: 'YAML all the way down. 2x production.',
    },
  ],
  [
    'cloudLocalSync',
    {
      id: 'cloudLocalSync',
      name: 'Cloud-Local Sync',
      cost: new Decimal(500000000),
      requirement: { type: 'buildingCount', buildingId: 'gpu', count: 40 },
      effect: { type: 'synergy', targetBuilding: 'cloudCluster', sourceBuilding: 'gpu', bonusPercentage: 1 },
      description: 'Hybrid deployment. +1% per GPU.',
    },
  ],

  // Server Rack upgrades
  [
    'h100s',
    {
      id: 'h100s',
      name: 'H100s',
      cost: new Decimal(100000000),
      requirement: { type: 'buildingCount', buildingId: 'serverRack', count: 5 },
      effect: { type: 'doubler', targetBuilding: 'serverRack' },
      description: 'The good GPUs. 2x production.',
    },
  ],
  [
    'infiniband',
    {
      id: 'infiniband',
      name: 'InfiniBand',
      cost: new Decimal(500000000),
      requirement: { type: 'buildingCount', buildingId: 'serverRack', count: 10 },
      effect: { type: 'doubler', targetBuilding: 'serverRack' },
      description: 'Fast interconnect. 2x production.',
    },
  ],
  [
    'liquidCooling',
    {
      id: 'liquidCooling',
      name: 'Liquid Cooling',
      cost: new Decimal(5000000000),
      requirement: { type: 'buildingCount', buildingId: 'serverRack', count: 25 },
      effect: { type: 'doubler', targetBuilding: 'serverRack' },
      description: 'Industrial grade. 2x production.',
    },
  ],
  [
    'onCallEngineer',
    {
      id: 'onCallEngineer',
      name: '24/7 On-Call Engineer',
      cost: new Decimal(50000000000),
      requirement: { type: 'buildingCount', buildingId: 'serverRack', count: 50 },
      effect: { type: 'doubler', targetBuilding: 'serverRack' },
      description: 'Pager duty. 2x production.',
    },
  ],
  [
    'rackDensity',
    {
      id: 'rackDensity',
      name: 'Rack Density',
      cost: new Decimal(5000000000),
      requirement: { type: 'buildingCount', buildingId: 'cloudCluster', count: 40 },
      effect: { type: 'synergy', targetBuilding: 'serverRack', sourceBuilding: 'cloudCluster', bonusPercentage: 1 },
      description: 'Learned from cloud deployments. +1% per Cloud Cluster.',
    },
  ],
  [
    'coloDeal',
    {
      id: 'coloDeal',
      name: 'Colo Deal',
      cost: new Decimal(500000000000),
      requirement: { type: 'buildingCount', buildingId: 'datacenter', count: 40 },
      effect: { type: 'synergy', targetBuilding: 'serverRack', sourceBuilding: 'datacenter', bonusPercentage: 1 },
      description: 'Bulk discount. +1% per Datacenter.',
    },
  ],

  // Datacenter upgrades
  [
    'nuclearPowerContract',
    {
      id: 'nuclearPowerContract',
      name: 'Nuclear Power Contract',
      cost: new Decimal(1000000000),
      requirement: { type: 'buildingCount', buildingId: 'datacenter', count: 5 },
      effect: { type: 'doubler', targetBuilding: 'datacenter' },
      description: 'Unlimited energy. 2x production.',
    },
  ],
  [
    'riverCooling',
    {
      id: 'riverCooling',
      name: 'River Cooling',
      cost: new Decimal(5000000000),
      requirement: { type: 'buildingCount', buildingId: 'datacenter', count: 10 },
      effect: { type: 'doubler', targetBuilding: 'datacenter' },
      description: 'Nature\'s heatsink. 2x production.',
    },
  ],
  [
    'customAsics',
    {
      id: 'customAsics',
      name: 'Custom ASICs',
      cost: new Decimal(50000000000),
      requirement: { type: 'buildingCount', buildingId: 'datacenter', count: 25 },
      effect: { type: 'doubler', targetBuilding: 'datacenter' },
      description: 'Purpose-built silicon. 2x production.',
    },
  ],
  [
    'acquiredStartupHardware',
    {
      id: 'acquiredStartupHardware',
      name: 'Acquired Startup\'s Hardware',
      cost: new Decimal(500000000000),
      requirement: { type: 'buildingCount', buildingId: 'datacenter', count: 50 },
      effect: { type: 'doubler', targetBuilding: 'datacenter' },
      description: 'Acqui-hire bonus. 2x production.',
    },
  ],
  [
    'centralizedTraining',
    {
      id: 'centralizedTraining',
      name: 'Centralized Training',
      cost: new Decimal(50000000000),
      requirement: { type: 'buildingCount', buildingId: 'codingAgent', count: 40 },
      effect: { type: 'synergy', targetBuilding: 'datacenter', sourceBuilding: 'codingAgent', bonusPercentage: 1 },
      description: 'All agents learn from DC. +1% per Coding Agent.',
    },
  ],
  [
    'verticalIntegration',
    {
      id: 'verticalIntegration',
      name: 'Vertical Integration',
      cost: new Decimal(10000000000),
      requirement: { type: 'buildingCount', buildingId: 'serverRack', count: 40 },
      effect: { type: 'synergy', targetBuilding: 'datacenter', sourceBuilding: 'serverRack', bonusPercentage: 1 },
      description: 'Own the whole stack. +1% per Server Rack.',
    },
  ],

  // Hyperscaler upgrades
  [
    'governmentContract',
    {
      id: 'governmentContract',
      name: 'Government Contract',
      cost: new Decimal(10000000000),
      requirement: { type: 'buildingCount', buildingId: 'hyperscaler', count: 5 },
      effect: { type: 'doubler', targetBuilding: 'hyperscaler' },
      description: 'Defense spending. 2x production.',
    },
  ],
  [
    'submarineCables',
    {
      id: 'submarineCables',
      name: 'Submarine Cables',
      cost: new Decimal(50000000000),
      requirement: { type: 'buildingCount', buildingId: 'hyperscaler', count: 10 },
      effect: { type: 'doubler', targetBuilding: 'hyperscaler' },
      description: 'Own the internet. 2x production.',
    },
  ],
  [
    'orbitalDatacenters',
    {
      id: 'orbitalDatacenters',
      name: 'Orbital Datacenters',
      cost: new Decimal(500000000000),
      requirement: { type: 'buildingCount', buildingId: 'hyperscaler', count: 25 },
      effect: { type: 'doubler', targetBuilding: 'hyperscaler' },
      description: 'No cooling needed in space. 2x production.',
    },
  ],
  [
    'fusionReactor',
    {
      id: 'fusionReactor',
      name: 'Fusion Reactor',
      cost: new Decimal(5000000000000),
      requirement: { type: 'buildingCount', buildingId: 'hyperscaler', count: 50 },
      effect: { type: 'doubler', targetBuilding: 'hyperscaler' },
      description: 'Infinite power. 2x production.',
    },
  ],
  [
    'globalNetworkEffect',
    {
      id: 'globalNetworkEffect',
      name: 'Global Network Effect',
      cost: new Decimal(500000000000),
      requirement: { type: 'buildingCount', buildingId: 'datacenter', count: 40 },
      effect: { type: 'synergy', targetBuilding: 'hyperscaler', sourceBuilding: 'datacenter', bonusPercentage: 1 },
      description: 'Your DCs feed the beast. +1% per Datacenter.',
    },
  ],
  [
    'agiEmergentBehavior',
    {
      id: 'agiEmergentBehavior',
      name: 'AGI Emergent Behavior',
      cost: new Decimal(10000000000000),
      requirement: { type: 'buildingCount', buildingId: 'codingAssistant', count: 40 },
      effect: { type: 'synergy', targetBuilding: 'hyperscaler', sourceBuilding: 'all', bonusPercentage: 1 },
      description: 'It\'s all connected now. +1% per building owned (all types).',
    },
  ],

  // Click scaling upgrades
  [
    'muscleMemory',
    {
      id: 'muscleMemory',
      name: 'Muscle Memory',
      cost: new Decimal(10000),
      requirement: { type: 'clickCount', count: 1000 },
      effect: { type: 'clickScaling', tpsPercentage: 1 },
      description: '+1% TPS per click.',
    },
  ],
  [
    'macroKeys',
    {
      id: 'macroKeys',
      name: 'Macro Keys',
      cost: new Decimal(1000000),
      requirement: { type: 'clickCount', count: 10000 },
      effect: { type: 'clickScaling', tpsPercentage: 5 },
      description: '+5% TPS per click.',
    },
  ],
  [
    'autoclickerTolerance',
    {
      id: 'autoclickerTolerance',
      name: 'Autoclicker Tolerance',
      cost: new Decimal(100000000),
      requirement: { type: 'clickCount', count: 100000 },
      effect: { type: 'clickScaling', tpsPercentage: 10 },
      description: '+10% TPS per click.',
    },
  ],
  [
    'neuralLink',
    {
      id: 'neuralLink',
      name: 'Neural Link',
      cost: new Decimal(10000000000),
      requirement: { type: 'clickCount', count: 1000000 },
      effect: { type: 'clickScaling', tpsPercentage: 25 },
      description: '+25% TPS per click.',
    },
  ],

  // AI Wrapper upgrades
  [
    'gradientLogo',
    {
      id: 'gradientLogo',
      name: 'Gradient Logo',
      cost: new Decimal(50000),
      requirement: { type: 'buildingCount', buildingId: 'modelWrapper', count: 10 },
      effect: { type: 'doubler', targetBuilding: 'modelWrapper' },
      description: 'Now we look like AI. 2x production.',
    },
  ],
  [
    'seriesAFunding',
    {
      id: 'seriesAFunding',
      name: 'Series A Funding',
      cost: new Decimal(250000),
      requirement: { type: 'buildingCount', buildingId: 'modelWrapper', count: 10 },
      effect: { type: 'doubler', targetBuilding: 'modelWrapper' },
      description: 'Money printer go brrr. 2x production.',
    },
  ],
  [
    'ybDemoDay',
    {
      id: 'ybDemoDay',
      name: 'YB Demo Day',
      cost: new Decimal(2500000),
      requirement: { type: 'buildingCount', buildingId: 'modelWrapper', count: 25 },
      effect: { type: 'doubler', targetBuilding: 'modelWrapper' },
      description: 'Two minute pitch, 2x returns. 2x production.',
    },
  ],
  [
    'rateLimitIncrease',
    {
      id: 'rateLimitIncrease',
      name: 'Rate Limit Increase',
      cost: new Decimal(12500000),
      requirement: { type: 'buildingCount', buildingId: 'modelWrapper', count: 50 },
      effect: { type: 'doubler', targetBuilding: 'modelWrapper' },
      description: 'Finally, more requests. 2x production.',
    },
  ],
  [
    'enterpriseTier',
    {
      id: 'enterpriseTier',
      name: 'Enterprise Tier',
      cost: new Decimal(125000000),
      requirement: { type: 'buildingCount', buildingId: 'modelWrapper', count: 100 },
      effect: { type: 'doubler', targetBuilding: 'modelWrapper' },
      description: 'Same API, 10x the price. 2x production.',
    },
  ],
  [
    'apiAggregator',
    {
      id: 'apiAggregator',
      name: 'API Aggregator',
      cost: new Decimal(25000000),
      requirement: { type: 'buildingCount', buildingId: 'codingAgent', count: 40 },
      effect: { type: 'synergy', targetBuilding: 'modelWrapper', sourceBuilding: 'codingAgent', bonusPercentage: 1 },
      description: 'Agents use your wrapper. +1% per Coding Agent.',
    },
  ],

  // Pear Pro upgrades
  [
    'switchToUsbC',
    {
      id: 'switchToUsbC',
      name: 'Switch to USB-C',
      cost: new Decimal(250000),
      requirement: { type: 'buildingCount', buildingId: 'pearPro', count: 10 },
      effect: { type: 'doubler', targetBuilding: 'pearPro' },
      description: 'Finally, one cable. 2x production.',
    },
  ],
  [
    'pearPodsMax',
    {
      id: 'pearPodsMax',
      name: 'PearPods Max',
      cost: new Decimal(1250000),
      requirement: { type: 'buildingCount', buildingId: 'pearPro', count: 10 },
      effect: { type: 'doubler', targetBuilding: 'pearPro' },
      description: 'Wireless everything. 2x production.',
    },
  ],
  [
    'dongleCollection',
    {
      id: 'dongleCollection',
      name: 'Dongle Collection',
      cost: new Decimal(12500000),
      requirement: { type: 'buildingCount', buildingId: 'pearPro', count: 25 },
      effect: { type: 'doubler', targetBuilding: 'pearPro' },
      description: 'Adapter for every occasion. 2x production.',
    },
  ],
  [
    'pearSiliconUltra',
    {
      id: 'pearSiliconUltra',
      name: 'Pear Silicon Ultra',
      cost: new Decimal(62500000),
      requirement: { type: 'buildingCount', buildingId: 'pearPro', count: 50 },
      effect: { type: 'doubler', targetBuilding: 'pearPro' },
      description: 'Even more unified memory. 2x production.',
    },
  ],
  [
    'pearIntelligence',
    {
      id: 'pearIntelligence',
      name: 'Pear Intelligence',
      cost: new Decimal(625000000),
      requirement: { type: 'buildingCount', buildingId: 'pearPro', count: 100 },
      effect: { type: 'doubler', targetBuilding: 'pearPro' },
      description: 'On-device AI. 2x production.',
    },
  ],
  [
    'creativeSuiteBundle',
    {
      id: 'creativeSuiteBundle',
      name: 'Creative Suite Bundle',
      cost: new Decimal(125000000),
      requirement: { type: 'buildingCount', buildingId: 'modelWrapper', count: 40 },
      effect: { type: 'synergy', targetBuilding: 'pearPro', sourceBuilding: 'modelWrapper', bonusPercentage: 1 },
      description: 'Designers love both. +1% per AI Wrapper.',
    },
  ],

  // TPU upgrades
  [
    'cloudCreditsPromo',
    {
      id: 'cloudCreditsPromo',
      name: 'Cloud Credits Promo',
      cost: new Decimal(64000000),
      requirement: { type: 'buildingCount', buildingId: 'tpu', count: 10 },
      effect: { type: 'doubler', targetBuilding: 'tpu' },
      description: 'Free tier maximized. 2x production.',
    },
  ],
  [
    'proNotebookTier',
    {
      id: 'proNotebookTier',
      name: 'Pro Notebook Tier',
      cost: new Decimal(320000000),
      requirement: { type: 'buildingCount', buildingId: 'tpu', count: 10 },
      effect: { type: 'doubler', targetBuilding: 'tpu' },
      description: 'More compute hours. 2x production.',
    },
  ],
  [
    'tensorFramework',
    {
      id: 'tensorFramework',
      name: 'Tensor Framework',
      cost: new Decimal(3200000000),
      requirement: { type: 'buildingCount', buildingId: 'tpu', count: 25 },
      effect: { type: 'doubler', targetBuilding: 'tpu' },
      description: 'Optimized operations. 2x production.',
    },
  ],
  [
    'batchJobPriority',
    {
      id: 'batchJobPriority',
      name: 'Batch Job Priority',
      cost: new Decimal(16000000000),
      requirement: { type: 'buildingCount', buildingId: 'tpu', count: 50 },
      effect: { type: 'doubler', targetBuilding: 'tpu' },
      description: 'Skip the queue. 2x production.',
    },
  ],
  [
    'bigDataPipeline',
    {
      id: 'bigDataPipeline',
      name: 'Big Data Pipeline',
      cost: new Decimal(160000000000),
      requirement: { type: 'buildingCount', buildingId: 'tpu', count: 100 },
      effect: { type: 'doubler', targetBuilding: 'tpu' },
      description: 'Petabytes flowing in. 2x production.',
    },
  ],
  [
    'mlPlatformIntegration',
    {
      id: 'mlPlatformIntegration',
      name: 'ML Platform Integration',
      cost: new Decimal(32000000000),
      requirement: { type: 'buildingCount', buildingId: 'customModel', count: 40 },
      effect: { type: 'synergy', targetBuilding: 'tpu', sourceBuilding: 'customModel', bonusPercentage: 1 },
      description: 'Platform synergy. +1% per Custom Model.',
    },
  ],

  // Custom Model upgrades
  [
    'fineTuningPipeline',
    {
      id: 'fineTuningPipeline',
      name: 'Fine-Tuning Pipeline',
      cost: new Decimal(4096000000),
      requirement: { type: 'buildingCount', buildingId: 'customModel', count: 10 },
      effect: { type: 'doubler', targetBuilding: 'customModel' },
      description: 'Your data, their weights. 2x production.',
    },
  ],
  [
    'rlhfTraining',
    {
      id: 'rlhfTraining',
      name: 'RLHF Training',
      cost: new Decimal(20480000000),
      requirement: { type: 'buildingCount', buildingId: 'customModel', count: 10 },
      effect: { type: 'doubler', targetBuilding: 'customModel' },
      description: 'Human feedback loop. 2x production.',
    },
  ],
  [
    'syntheticData',
    {
      id: 'syntheticData',
      name: 'Synthetic Data',
      cost: new Decimal(204800000000),
      requirement: { type: 'buildingCount', buildingId: 'customModel', count: 25 },
      effect: { type: 'doubler', targetBuilding: 'customModel' },
      description: 'Models training models. 2x production.',
    },
  ],
  [
    'benchmarkGaming',
    {
      id: 'benchmarkGaming',
      name: 'Benchmark Gaming',
      cost: new Decimal(1024000000000),
      requirement: { type: 'buildingCount', buildingId: 'customModel', count: 50 },
      effect: { type: 'doubler', targetBuilding: 'customModel' },
      description: 'SOTA on paper. 2x production.',
    },
  ],
  [
    'openWeightsRelease',
    {
      id: 'openWeightsRelease',
      name: 'Open Weights Release',
      cost: new Decimal(10240000000000),
      requirement: { type: 'buildingCount', buildingId: 'customModel', count: 100 },
      effect: { type: 'doubler', targetBuilding: 'customModel' },
      description: 'Community goodwill. 2x production.',
    },
  ],
  [
    'emergentAbilities',
    {
      id: 'emergentAbilities',
      name: 'Emergent Abilities',
      cost: new Decimal(81920000000000),
      requirement: { type: 'buildingCount', buildingId: 'customModel', count: 150 },
      effect: { type: 'doubler', targetBuilding: 'customModel' },
      description: "We don't know why it works. 2x production.",
    },
  ],
  [
    'distillationPipeline',
    {
      id: 'distillationPipeline',
      name: 'Distillation Pipeline',
      cost: new Decimal(2048000000000),
      requirement: { type: 'buildingCount', buildingId: 'codingAgent', count: 40 },
      effect: { type: 'synergy', targetBuilding: 'customModel', sourceBuilding: 'codingAgent', bonusPercentage: 1 },
      description: 'Agents distill knowledge. +1% per Coding Agent.',
    },
  ],

  // Atomic Energy upgrades
  [
    'gridPriorityAccess',
    {
      id: 'gridPriorityAccess',
      name: 'Grid Priority Access',
      cost: new Decimal(262144000000),
      requirement: { type: 'buildingCount', buildingId: 'atomicEnergy', count: 10 },
      effect: { type: 'doubler', targetBuilding: 'atomicEnergy' },
      description: 'Skip the power queue. 2x production.',
    },
  ],
  [
    'smrFleet',
    {
      id: 'smrFleet',
      name: 'SMR Fleet',
      cost: new Decimal(1310720000000),
      requirement: { type: 'buildingCount', buildingId: 'atomicEnergy', count: 10 },
      effect: { type: 'doubler', targetBuilding: 'atomicEnergy' },
      description: 'Small modular everywhere. 2x production.',
    },
  ],
  [
    'thoriumReactors',
    {
      id: 'thoriumReactors',
      name: 'Thorium Reactors',
      cost: new Decimal(13107200000000),
      requirement: { type: 'buildingCount', buildingId: 'atomicEnergy', count: 25 },
      effect: { type: 'doubler', targetBuilding: 'atomicEnergy' },
      description: 'The better nuclear. 2x production.',
    },
  ],
  [
    'legacyPlantRevival',
    {
      id: 'legacyPlantRevival',
      name: 'Legacy Plant Revival',
      cost: new Decimal(65536000000000),
      requirement: { type: 'buildingCount', buildingId: 'atomicEnergy', count: 50 },
      effect: { type: 'doubler', targetBuilding: 'atomicEnergy' },
      description: 'Decommissioned no more. 2x production.',
    },
  ],
  [
    'fusionBreakthrough',
    {
      id: 'fusionBreakthrough',
      name: 'Fusion Breakthrough',
      cost: new Decimal(655360000000000),
      requirement: { type: 'buildingCount', buildingId: 'atomicEnergy', count: 100 },
      effect: { type: 'doubler', targetBuilding: 'atomicEnergy' },
      description: 'Only 10 more years. 2x production.',
    },
  ],
  [
    'datacenterPowerDeal',
    {
      id: 'datacenterPowerDeal',
      name: 'Datacenter Power Deal',
      cost: new Decimal(131072000000000),
      requirement: { type: 'buildingCount', buildingId: 'datacenter', count: 40 },
      effect: { type: 'synergy', targetBuilding: 'atomicEnergy', sourceBuilding: 'datacenter', bonusPercentage: 1 },
      description: 'Bulk power discount. +1% per Datacenter.',
    },
  ],

  // Quantum Compute upgrades
  [
    'researchLabPartnership',
    {
      id: 'researchLabPartnership',
      name: 'Research Lab Partnership',
      cost: new Decimal(16777216000000),
      requirement: { type: 'buildingCount', buildingId: 'quantumCompute', count: 10 },
      effect: { type: 'doubler', targetBuilding: 'quantumCompute' },
      description: 'They have the hardware. 2x production.',
    },
  ],
  [
    'supercooledChambers',
    {
      id: 'supercooledChambers',
      name: 'Supercooled Chambers',
      cost: new Decimal(83886080000000),
      requirement: { type: 'buildingCount', buildingId: 'quantumCompute', count: 10 },
      effect: { type: 'doubler', targetBuilding: 'quantumCompute' },
      description: 'Near absolute zero. 2x production.',
    },
  ],
  [
    'qubitScaling',
    {
      id: 'qubitScaling',
      name: 'Qubit Scaling',
      cost: new Decimal(838860800000000),
      requirement: { type: 'buildingCount', buildingId: 'quantumCompute', count: 25 },
      effect: { type: 'doubler', targetBuilding: 'quantumCompute' },
      description: 'More is more. 2x production.',
    },
  ],
  [
    'errorCorrection',
    {
      id: 'errorCorrection',
      name: 'Error Correction',
      cost: new Decimal(4194304000000000),
      requirement: { type: 'buildingCount', buildingId: 'quantumCompute', count: 50 },
      effect: { type: 'doubler', targetBuilding: 'quantumCompute' },
      description: 'Finally useful qubits. 2x production.',
    },
  ],
  [
    'quantumSupremacy',
    {
      id: 'quantumSupremacy',
      name: 'Quantum Supremacy',
      cost: new Decimal(41943040000000000),
      requirement: { type: 'buildingCount', buildingId: 'quantumCompute', count: 100 },
      effect: { type: 'doubler', targetBuilding: 'quantumCompute' },
      description: 'Beat classical (once). 2x production.',
    },
  ],
  [
    'hybridClassical',
    {
      id: 'hybridClassical',
      name: 'Hybrid Classical',
      cost: new Decimal(8388608000000000),
      requirement: { type: 'buildingCount', buildingId: 'cloudCluster', count: 40 },
      effect: { type: 'synergy', targetBuilding: 'quantumCompute', sourceBuilding: 'cloudCluster', bonusPercentage: 1 },
      description: 'Best of both worlds. +1% per Cloud Cluster.',
    },
  ],

  // Dyson Sphere upgrades
  [
    'stellarLifting',
    {
      id: 'stellarLifting',
      name: 'Stellar Lifting',
      cost: new Decimal(134217728000000),
      requirement: { type: 'buildingCount', buildingId: 'dysonSphere', count: 10 },
      effect: { type: 'doubler', targetBuilding: 'dysonSphere' },
      description: 'Mining the sun itself. 2x production.',
    },
  ],
  [
    'vonNeumannProbes',
    {
      id: 'vonNeumannProbes',
      name: 'Von Neumann Probes',
      cost: new Decimal(671088640000000),
      requirement: { type: 'buildingCount', buildingId: 'dysonSphere', count: 10 },
      effect: { type: 'doubler', targetBuilding: 'dysonSphere' },
      description: 'Self-replicating fleet. 2x production.',
    },
  ],
  [
    'matrioshkaBrain',
    {
      id: 'matrioshkaBrain',
      name: 'Matrioshka Brain',
      cost: new Decimal(6710886400000000),
      requirement: { type: 'buildingCount', buildingId: 'dysonSphere', count: 25 },
      effect: { type: 'doubler', targetBuilding: 'dysonSphere' },
      description: 'Nested compute shells. 2x production.',
    },
  ],
  [
    'ftlNetwork',
    {
      id: 'ftlNetwork',
      name: 'FTL Network',
      cost: new Decimal(33554432000000000),
      requirement: { type: 'buildingCount', buildingId: 'dysonSphere', count: 50 },
      effect: { type: 'doubler', targetBuilding: 'dysonSphere' },
      description: 'Latency? What latency? 2x production.',
    },
  ],
  [
    'typeIIICivilization',
    {
      id: 'typeIIICivilization',
      name: 'Type III Civilization',
      cost: new Decimal(335544320000000000),
      requirement: { type: 'buildingCount', buildingId: 'dysonSphere', count: 100 },
      effect: { type: 'doubler', targetBuilding: 'dysonSphere' },
      description: 'Galaxy-scale operations. 2x production.',
    },
  ],
  [
    'galacticExpansion',
    {
      id: 'galacticExpansion',
      name: 'Galactic Expansion',
      cost: new Decimal(67108864000000000),
      requirement: { type: 'buildingCount', buildingId: 'quantumCompute', count: 40 },
      effect: { type: 'synergy', targetBuilding: 'dysonSphere', sourceBuilding: 'quantumCompute', bonusPercentage: 1 },
      description: 'Quantum-powered expansion. +1% per Quantum Compute.',
    },
  ],

  // Additional Datacenter upgrades
  [
    'underwaterCenters',
    {
      id: 'underwaterCenters',
      name: 'Underwater Centers',
      cost: new Decimal(327680000000),
      requirement: { type: 'buildingCount', buildingId: 'datacenter', count: 75 },
      effect: { type: 'doubler', targetBuilding: 'datacenter' },
      description: 'Ocean-cooled compute. 2x production.',
    },
  ],
  [
    'spaceCooling',
    {
      id: 'spaceCooling',
      name: 'Space Cooling',
      cost: new Decimal(655360000000),
      requirement: { type: 'buildingCount', buildingId: 'datacenter', count: 100 },
      effect: { type: 'doubler', targetBuilding: 'datacenter' },
      description: 'No atmosphere, no heat. 2x production.',
    },
  ],

  // Additional Hyperscaler upgrades
  [
    'closedAIPartnership',
    {
      id: 'closedAIPartnership',
      name: 'ClosedAI Partnership',
      cost: new Decimal(20971520000000),
      requirement: { type: 'buildingCount', buildingId: 'hyperscaler', count: 75 },
      effect: { type: 'doubler', targetBuilding: 'hyperscaler' },
      description: 'Strategic alignment. 2x production.',
    },
  ],
  [
    'circularDebtFlows',
    {
      id: 'circularDebtFlows',
      name: 'Circular Debt Flows',
      cost: new Decimal(41943040000000),
      requirement: { type: 'buildingCount', buildingId: 'hyperscaler', count: 100 },
      effect: { type: 'doubler', targetBuilding: 'hyperscaler' },
      description: 'Money goes round and round. 2x production.',
    },
  ],
])

export const getUpgrade = (id: UpgradeId): UpgradeDefinition | undefined =>
  upgrades.get(id)

export const getAllUpgradeIds = (): UpgradeId[] => Array.from(upgrades.keys())
